import {
  deriveConservativeFoodClassification,
  normalizeAllergenCodes,
  normalizeUserDietaryPreference,
} from "../foods/foodClassification";
import { getLocalizedAllergenLabel } from "./localizedAllergens";

export const ASSIGNMENT_CONFLICT_STATE_NONE = "none";
export const ASSIGNMENT_CONFLICT_STATE_UNRESOLVED = "unresolved";
export const ASSIGNMENT_CONFLICT_STATE_ACCEPTED = "accepted";
export const ASSIGNMENT_CONFLICT_STATE_REMOVED = "removed";

function getClientRestrictionSource(client) {
  const settings =
    client?.clientSettings && typeof client.clientSettings === "object"
      ? client.clientSettings
      : {};

  return {
    dietaryPreference: normalizeUserDietaryPreference(
      settings.dietary_preference ?? client?.dietaryPreference,
    ),
    allergenCodes: normalizeAllergenCodes(
      settings.allergen_codes ?? client?.allergenCodes,
    ),
  };
}

function createConflictSignalState() {
  return {
    allergenCodes: new Set(),
    hasNonVegetarian: false,
    hasNonVegan: false,
  };
}

function collectConflictSignals(source, state = createConflictSignalState()) {
  if (!source || typeof source !== "object") {
    return state;
  }
  const classification = deriveConservativeFoodClassification(source);
  classification.allergen_codes.forEach((code) => state.allergenCodes.add(code));

  if (classification.dietary_data_complete === true) {
    if (classification.is_vegetarian !== true) {
      state.hasNonVegetarian = true;
    }
    if (classification.is_vegan !== true) {
      state.hasNonVegan = true;
    }
  }

  return state;
}

export function buildNutritionConflictReasonLabel(reasonCode, t) {
  const normalizedReasonCode = String(reasonCode ?? "").trim().toLowerCase();
  if (!normalizedReasonCode) {
    return "";
  }

  if (normalizedReasonCode === "not_vegan") {
    return typeof t === "function"
      ? t("nutritionConflictReasonNotVegan-string", "Not vegan")
      : "Not vegan";
  }

  if (normalizedReasonCode === "not_vegetarian") {
    return typeof t === "function"
      ? t("nutritionConflictReasonNotVegetarian-string", "Not vegetarian")
      : "Not vegetarian";
  }

  return typeof t === "function"
    ? t("nutritionConflictReasonContains-string", "Contains {{item}}", {
        item: getLocalizedAllergenLabel(normalizedReasonCode, t).toLowerCase(),
      })
    : `Contains ${getLocalizedAllergenLabel(normalizedReasonCode, t).toLowerCase()}`;
}

export function getNutritionConflictSummary({ client, source, t } = {}) {
  const restrictions = getClientRestrictionSource(client);
  if (!source || restrictions == null) {
    return {
      hasConflicts: false,
      reasons: [],
    };
  }

  const signals = collectConflictSignals(source);
  const reasons = [];

  restrictions.allergenCodes.forEach((allergenCode) => {
    if (signals.allergenCodes.has(allergenCode)) {
      reasons.push(buildNutritionConflictReasonLabel(allergenCode, t));
    }
  });

  if (restrictions.dietaryPreference === "vegan" && signals.hasNonVegan) {
    reasons.push(buildNutritionConflictReasonLabel("not_vegan", t));
  } else if (
    restrictions.dietaryPreference === "vegetarian" &&
    signals.hasNonVegetarian
  ) {
    reasons.push(buildNutritionConflictReasonLabel("not_vegetarian", t));
  }

  return {
    hasConflicts: reasons.length > 0,
    reasons,
  };
}

export function getNutritionConflictDescription(summary) {
  return Array.isArray(summary?.reasons) ? summary.reasons.join(" • ") : "";
}

export function getAssignmentConflictResolutionStatus({
  clientId,
  selectedClientIds = [],
  acceptedConflictClientIds = {},
  conflictSummariesByClientId = {},
} = {}) {
  const normalizedClientId = String(clientId ?? "").trim().toLowerCase();
  if (!normalizedClientId) {
    return ASSIGNMENT_CONFLICT_STATE_NONE;
  }

  const resolutionState = String(
    acceptedConflictClientIds?.[normalizedClientId] ?? "",
  )
    .trim()
    .toLowerCase();
  const isSelected = selectedClientIds.includes(normalizedClientId);
  const hasConflicts = conflictSummariesByClientId?.[normalizedClientId]?.hasConflicts === true;

  if (!isSelected) {
    return resolutionState === ASSIGNMENT_CONFLICT_STATE_REMOVED
      ? ASSIGNMENT_CONFLICT_STATE_REMOVED
      : ASSIGNMENT_CONFLICT_STATE_NONE;
  }

  if (!hasConflicts) {
    return ASSIGNMENT_CONFLICT_STATE_NONE;
  }

  return resolutionState === ASSIGNMENT_CONFLICT_STATE_ACCEPTED
    ? ASSIGNMENT_CONFLICT_STATE_ACCEPTED
    : ASSIGNMENT_CONFLICT_STATE_UNRESOLVED;
}

export function getUnresolvedAssignmentConflictClientIds({
  selectedClientIds = [],
  acceptedConflictClientIds = {},
  conflictSummariesByClientId = {},
} = {}) {
  return selectedClientIds.filter(
    (clientId) =>
      getAssignmentConflictResolutionStatus({
        clientId,
        selectedClientIds,
        acceptedConflictClientIds,
        conflictSummariesByClientId,
      }) === ASSIGNMENT_CONFLICT_STATE_UNRESOLVED,
  );
}
